import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Star, Quote, ShieldCheck, Truck, ChevronDown } from 'lucide-react';
import { Seo } from '@/components/Seo';
import NotFound from '@/pages/NotFound';
import { Spinner } from '@/components/ui/EmptyState';
import { supabase } from '@/lib/supabaseClient';
import { useSiteImage } from '@/hooks/useSiteImage';
import { useCart } from '@/contexts/CartContext';
import { useProducts } from '@/contexts/ProductsContext';
import { trackViewContent, trackAddToCart } from '@/lib/tracking';
import type { LandingBlock, LandingPage } from '@/types/landingPage';
import type { Product, RoutineSet } from '@/types/product';

// bundle_sku is a cart "slug" — either the 3-step bundle ('3-step-set',
// resolved via routineSet, the only one with was/now bundle pricing) or a
// single product's own slug (resolved by matching Product.id, e.g.
// 'daily-rescue-serum'). Returns null if it matches neither — the cta
// block then renders without a live price and Buy still adds the slug to
// cart, but with no price shown (better than showing something wrong).
function resolveLiveProduct(
  bundleSku: string | null,
  routineSet: RoutineSet | null,
  products: Product[],
): { name: string; price: string; originalPrice?: string } | null {
  if (!bundleSku) return null;
  if (bundleSku === '3-step-set') return routineSet;
  const p = products.find((prod) => prod.id === bundleSku);
  return p ? { name: p.name, price: p.price } : null;
}

// YouTube/Vimeo watch links need converting to their embeddable form;
// anything else is assumed to be a direct video file URL for a plain
// <video> tag.
function videoEmbedUrl(url: string): string | null {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
}

function BuyButton({ label, buying, onBuy, full }: { label: string; buying: boolean; onBuy: () => void; full?: boolean }) {
  return (
    <button
      onClick={onBuy}
      disabled={buying}
      className={`${full ? 'w-full' : 'inline-block px-10'} py-4 bg-[#7B1113] text-white text-[13px] font-semibold tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] active:scale-[0.98] transition-all cursor-pointer disabled:opacity-60 shadow-[0_8px_24px_-8px_rgba(123,17,19,0.5)]`}
    >
      {buying ? 'Adding…' : label}
    </button>
  );
}

function LandingBlockView({
  block,
  onBuy,
  buying,
  liveProduct,
}: {
  block: LandingBlock;
  onBuy: () => void;
  buying: boolean;
  liveProduct: { name: string; price: string; originalPrice?: string } | null;
}) {
  switch (block.type) {
    case 'hero':
      return (
        <div className="text-center space-y-5 pt-8 pb-4 md:pt-14">
          {block.badge && (
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#7B1113]/8 text-[#7B1113] text-[10.5px] tracking-[0.18em] uppercase font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-[#7B1113]" />
              {block.badge}
            </span>
          )}
          <h1 className="font-serif font-bold text-[#211F1E] text-[34px] leading-[1.15] sm:text-5xl md:text-6xl md:leading-[1.1] text-balance tracking-tight px-1">
            {block.heading}
          </h1>
          {block.subheading && (
            <p className="text-[15px] md:text-xl text-[#6B6B68] font-light max-w-xl mx-auto leading-relaxed px-2">{block.subheading}</p>
          )}
          {block.imageUrl && (
            <div className="pt-3 md:pt-6">
              <img
                src={block.imageUrl}
                alt={block.heading}
                className="w-full max-w-2xl mx-auto rounded-[28px] shadow-[0_30px_60px_-20px_rgba(33,31,30,0.25)] ring-1 ring-black/5"
              />
            </div>
          )}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 pt-5 text-[11.5px] text-[#8A8A87] font-medium">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#7B1113]" /> Dermatologist Formulated</span>
            <span className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-[#7B1113]" /> Cash on Delivery Available</span>
          </div>
        </div>
      );
    case 'heading':
      return (
        <div className="text-center space-y-2.5 pt-2">
          <h2
            className={`font-serif font-bold text-[#211F1E] text-balance ${block.size === 'xl' ? 'text-3xl sm:text-4xl md:text-5xl' : 'text-2xl sm:text-3xl'}`}
          >
            {block.text}
          </h2>
          <div className="w-10 h-[3px] bg-[#7B1113] rounded-full mx-auto" />
        </div>
      );
    case 'paragraph':
      return (
        <p className="text-[15px] md:text-base text-[#5A5A57] font-light leading-[1.85] whitespace-pre-line max-w-2xl mx-auto text-center">
          {block.text}
        </p>
      );
    case 'image':
      if (!block.url) return null;
      return (
        <figure className="max-w-2xl mx-auto w-full">
          <img src={block.url} alt={block.alt} className="w-full h-auto rounded-[24px] shadow-[0_20px_50px_-20px_rgba(33,31,30,0.2)] ring-1 ring-black/5" />
          {block.caption && <figcaption className="text-xs text-[#9A9A97] mt-3 text-center">{block.caption}</figcaption>}
        </figure>
      );
    case 'video': {
      if (!block.url) return null;
      const embed = videoEmbedUrl(block.url);
      return (
        <figure className={`${block.aspect === 'portrait' ? 'max-w-[340px]' : 'max-w-2xl'} mx-auto w-full`}>
          <div className={`relative w-full ${block.aspect === 'portrait' ? 'aspect-[9/16]' : 'aspect-video'} rounded-[24px] overflow-hidden bg-black shadow-[0_20px_50px_-20px_rgba(33,31,30,0.3)] ring-1 ring-black/5`}>
            {embed ? (
              <iframe src={embed} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen title={block.caption || 'Video'} />
            ) : (
              <video src={block.url} poster={block.posterUrl} controls playsInline preload="metadata" className="w-full h-full object-cover" />
            )}
          </div>
          {block.caption && <figcaption className="text-xs text-[#9A9A97] mt-3 text-center">{block.caption}</figcaption>}
        </figure>
      );
    }
    case 'testimonials':
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {block.items.map((item, i) => (
            <div
              key={i}
              className="rounded-[20px] border border-[#EDEBE8] p-5 md:p-6 space-y-3 bg-white shadow-[0_4px_16px_-8px_rgba(33,31,30,0.08)] hover:shadow-[0_12px_28px_-10px_rgba(33,31,30,0.16)] transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-3.5 h-3.5 ${n <= item.rating ? 'fill-[#D4A574] text-[#D4A574]' : 'text-[#E8E8E6]'}`} />
                  ))}
                </div>
                <Quote className="w-5 h-5 text-[#E8E8E6]" />
              </div>
              <p className="text-[14.5px] text-[#3A3A38] leading-relaxed font-light">{item.quote}</p>
              <div className="flex items-center gap-2.5 pt-1.5 border-t border-[#F2F1EF] mt-1">
                {item.photoUrl ? (
                  <img src={item.photoUrl} alt={item.name} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-[#F2F1EF] flex-shrink-0" />
                )}
                <span className="text-[13px] font-semibold text-[#211F1E]">{item.name}</span>
              </div>
            </div>
          ))}
        </div>
      );
    case 'faq':
      return (
        <div className="space-y-2.5 max-w-2xl mx-auto w-full">
          {block.items.map((item, i) => (
            <details key={i} className="rounded-2xl border border-[#EDEBE8] px-5 py-1 group bg-white [&_summary::-webkit-details-marker]:hidden">
              <summary className="flex items-center justify-between gap-3 py-3.5 font-semibold text-[#211F1E] cursor-pointer text-[14.5px] md:text-base list-none">
                {item.question}
                <ChevronDown className="w-4 h-4 text-[#7B1113] flex-shrink-0 transition-transform group-open:rotate-180" />
              </summary>
              <p className="text-[13.5px] text-[#5A5A57] font-light pb-4 -mt-1 leading-relaxed">{item.answer}</p>
            </details>
          ))}
        </div>
      );
    case 'cta':
      return (
        <div className="rounded-[28px] bg-gradient-to-br from-[#211F1E] to-[#3A2426] text-white p-7 md:p-12 text-center space-y-5 shadow-[0_30px_60px_-20px_rgba(33,31,30,0.4)]">
          <h3 className="font-serif font-bold text-2xl md:text-4xl text-balance">{block.headline}</h3>
          {liveProduct && (
            <div className="flex items-center justify-center gap-3">
              <span className="font-serif text-3xl md:text-4xl font-semibold">{liveProduct.price}</span>
              {liveProduct.originalPrice && <span className="text-white/35 line-through text-lg md:text-xl">{liveProduct.originalPrice}</span>}
            </div>
          )}
          <div className="pt-1 max-w-xs mx-auto sm:max-w-none">
            <BuyButton label={block.buttonLabel} buying={buying} onBuy={onBuy} full />
          </div>
          <p className="text-[11.5px] text-white/45 tracking-wide">Cash on Delivery available &middot; Ships within 24 hours</p>
        </div>
      );
  }
}

export default function LandingPageRenderer() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { routineSet, products, loading: productsLoading } = useProducts();
  const [page, setPage] = useState<LandingPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const hasTrackedView = useRef(false);
  const liveProduct = resolveLiveProduct(page?.bundle_sku ?? null, routineSet, products);
  // Same key SharedNavbar uses — whatever logo is set there (admin-editable
  // via Site Settings) shows here too, automatically staying in sync.
  const logoUrl = useSiteImage('nav.logo', 'https://public.readdy.ai/ai/img_res/f8e8e9d8-9748-4109-8994-aa9e580acb44.png');

  useEffect(() => {
    if (!slug) return;
    let mounted = true;
    supabase
      .from('rxk_landing_pages')
      .select('id, slug, title, status, bundle_sku, blocks, seo_description')
      .eq('slug', slug)
      .eq('status', 'published')
      .maybeSingle()
      .then(({ data }) => {
        if (mounted) {
          setPage((data as LandingPage) ?? null);
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [slug]);

  useEffect(() => {
    if (hasTrackedView.current) return;
    if (!page || productsLoading) return;
    if (page.bundle_sku && !liveProduct) return; // still resolving — wait for products/routineSet to load
    hasTrackedView.current = true;
    if (page.bundle_sku && liveProduct) {
      trackViewContent({ id: page.bundle_sku, name: liveProduct.name, price: Number(liveProduct.price.replace(/[^0-9.]/g, '')) });
    }
  }, [page, productsLoading, liveProduct]);

  // A persistent mobile buy bar — the single highest-impact addition for
  // conversion on a scrolling ad page: the CTA stays reachable without
  // scrolling back down, no matter how far into the content someone reads.
  // Appears once they've scrolled past the hero, desktop hides it (the
  // hero/final CTA are already close enough there not to need it).
  useEffect(() => {
    const onScroll = () => setShowStickyBar(window.scrollY > 480);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleBuy = () => {
    if (!page?.bundle_sku) return;
    setBuying(true);
    addToCart(page.bundle_sku, 1);
    if (liveProduct) {
      trackAddToCart({ id: page.bundle_sku, name: liveProduct.name, price: Number(liveProduct.price.replace(/[^0-9.]/g, '')), quantity: 1 });
    }
    navigate('/checkout');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (!page) return <NotFound />;

  return (
    <>
      <Seo title={`${page.title} | Rx.K Seoul`} description={page.seo_description ?? undefined} noindex />
      <div className="min-h-screen bg-[#FBFAF8]">
        <header className="sticky top-0 z-20 bg-white/85 backdrop-blur-md border-b border-[#EDEBE8] py-3 flex items-center justify-center">
          <img src={logoUrl} alt="Rx.K Seoul" className="h-7 md:h-8 w-auto" />
        </header>
        <main className="max-w-[560px] md:max-w-2xl mx-auto px-4 sm:px-6 py-8 md:py-12 flex flex-col gap-12 md:gap-16">
          {page.blocks.map((block, i) => (
            <LandingBlockView key={i} block={block} onBuy={handleBuy} buying={buying} liveProduct={liveProduct} />
          ))}
        </main>
        <footer className="py-10 text-center text-[11px] text-[#9A9A97] px-4">
          <p>&copy; {new Date().getFullYear()} Rx.K Seoul. Secure checkout &middot; Cash on Delivery available.</p>
        </footer>

        {/* Sticky mobile buy bar */}
        {liveProduct && (
          <div
            className={`md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-md border-t border-[#EDEBE8] px-4 py-3 flex items-center justify-between gap-3 shadow-[0_-8px_24px_-8px_rgba(33,31,30,0.12)] transition-transform duration-300 ${
              showStickyBar ? 'translate-y-0' : 'translate-y-full'
            }`}
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
          >
            <div className="min-w-0">
              <p className="text-[11px] text-[#8A8A87] truncate">{liveProduct.name}</p>
              <p className="font-serif font-bold text-[#211F1E] text-base">{liveProduct.price}</p>
            </div>
            <button
              onClick={handleBuy}
              disabled={buying}
              className="flex-shrink-0 px-6 py-3 bg-[#7B1113] text-white text-[12px] font-semibold tracking-[0.1em] uppercase rounded-full hover:bg-[#5C0D0F] active:scale-[0.98] transition-all cursor-pointer disabled:opacity-60"
            >
              {buying ? 'Adding…' : 'Buy Now'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
